import {
  PreferredReturnAccrualMethod,
  PreferredReturnCalculationEnd,
} from 'further-types/lp/carry-settings';
import { carrySettings } from 'further-ui/validations';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { z } from 'zod';

type FormValues = z.infer<
  typeof carrySettings.CarriedInterestFeeTemplatesSettings
>;

export default function useCarriedInterestFeeTemplate(templateIndex: number) {
  const { setValue, watch, control } = useFormContext<FormValues>();
  const carriedInterestFeeTemplates = watch('carriedInterestFeeTemplates');

  const hasOtherDefaultTemplate = carriedInterestFeeTemplates.some(
    (template, index) => template.isDefault && templateIndex !== index,
  );

  const useCompoundAccrualMethod =
    carriedInterestFeeTemplates[templateIndex].preferredReturnAccrualMethod ===
    PreferredReturnAccrualMethod.Compound;

  const useSimpleAccrualMethod =
    carriedInterestFeeTemplates[templateIndex].preferredReturnAccrualMethod ===
    PreferredReturnAccrualMethod.Simple;

  const handleChangePreferredReturnAccrualMethod = (
    value: PreferredReturnAccrualMethod,
  ) => {
    setValue(
      `carriedInterestFeeTemplates.${templateIndex}.preferredReturnAccrualMethod`,
      value,
    );

    if (value === PreferredReturnAccrualMethod.Simple) {
      setValue(
        `carriedInterestFeeTemplates.${templateIndex}.preferredReturnCalculationEnd`,
        PreferredReturnCalculationEnd.UponFullReturnOfCapital,
      );
      setValue(
        `carriedInterestFeeTemplates.${templateIndex}.preferredReturnCompoundingPeriod`,
        undefined,
      );
    }
  };

  const { fields, append, remove } = useFieldArray({
    control,
    name: `carriedInterestFeeTemplates.${templateIndex}.carriedInterestRates`,
  });

  const handleCarriedInterestRatesReorder = (
    orderedFieldIds: Array<string>,
  ) => {
    const currentIndexes = orderedFieldIds.map((id) =>
      fields.findIndex((field) => field.id === id),
    );

    setValue(
      `carriedInterestFeeTemplates.${templateIndex}.carriedInterestRates`,
      currentIndexes.map(
        (rateIndex) =>
          carriedInterestFeeTemplates[templateIndex].carriedInterestRates[
            rateIndex
          ],
      ),
    );
  };

  return {
    handleChangePreferredReturnAccrualMethod,
    hasOtherDefaultTemplate,
    useCompoundAccrualMethod,
    useSimpleAccrualMethod,
    carriedInterestRateFields: fields,
    appendCarriedInterestRate: append,
    removeCarriedInterestRate: remove,
    handleCarriedInterestRatesReorder,
  };
}
